import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../context/NotificationContext';
import './HotelAssignmentModal.css';

function HotelAssignmentModal({ isOpen, onClose, onSave, compSet, allHotels }) {
    const [assignedIds, setAssignedIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { showNotification } = useNotification();

    const getAuthHeader = useCallback(() => {
        const token = localStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
    }, []);

    useEffect(() => {
        if (isOpen && compSet) {
            const fetchAssignedHotels = async () => {
                setLoading(true);
                setError('');
                try {
                    const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets/${compSet.comp_set_id}/hotels`;
                    const assignedResponse = await fetch(apiUrl, { headers: getAuthHeader() });
                    const assignedData = await assignedResponse.json();
                    
                    if (assignedData.success && Array.isArray(assignedData.data)) {
                        const numericIds = assignedData.data.map(id => Number(id));
                        setAssignedIds(new Set(numericIds));
                    } else {
                        throw new Error(assignedData.error?.message || 'Failed to fetch assigned hotels.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchAssignedHotels();
        }
    }, [isOpen, compSet, getAuthHeader]);

    const handleAssign = (hotelId) => {
        setAssignedIds(prev => new Set(prev).add(Number(hotelId)));
    };

    const handleUnassign = (hotelId) => {
        setAssignedIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(Number(hotelId));
            return newSet;
        });
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets/${compSet.comp_set_id}/hotels`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: getAuthHeader(),
                body: JSON.stringify({ hotel_ids: Array.from(assignedIds) })
            });
            const result = await response.json();
            if (response.ok && result.success) {
                showNotification(result.data.message || 'Asignaciones guardadas!', 'success');
                onSave();
                onClose();
            } else {
                throw new Error(result.error?.message || 'Ocurrió un error desconocido.');
            }
        } catch (err) {
            setError(err.message);
            showNotification(`Error al guardar: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };
    
    if (!isOpen) return null;

    const assignedHotels = allHotels.filter(h => assignedIds.has(Number(h.hotel_id)));
    const availableHotels = allHotels.filter(h => !assignedIds.has(Number(h.hotel_id)));
    const groupedAvailable = availableHotels.reduce((acc, hotel) => {
        const city = hotel.city || 'Sin Ciudad';
        if (!acc[city]) { acc[city] = []; }
        acc[city].push(hotel);
        return acc;
    }, {});


    return (
        <div className="modal-overlay">
            <div className="modal-content assignment-modal">
                <div className="modal-header">
                    <h2>Administrar Hoteles para "{compSet?.name}"</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {loading && <p>Cargando hoteles...</p>}
                    {error && <p className="error-message">{error}</p>}
                    {!loading && !error && (
                        <div className="assignment-container">
                            <div className="hotel-list-box">
                                <h3>Hoteles Disponibles</h3>
                                <ul className="scrollable-list">
                                    {Object.keys(groupedAvailable).sort().map(city => (
                                        <div key={city}>
                                            <li className="city-header">{city}</li>
                                            {groupedAvailable[city].map(hotel => (
                                                <li key={hotel.hotel_id} className="hotel-item" onClick={() => handleAssign(hotel.hotel_id)}>
                                                    {hotel.name} <span>+</span>
                                                </li>
                                            ))}
                                        </div>
                                    ))}
                                </ul>
                            </div>
                            <div className="hotel-list-box">
                                <h3>Hoteles en este Comp Set</h3>
                                <ul className="scrollable-list">
                                    {assignedHotels.map(hotel => (
                                        <li key={hotel.hotel_id} className="hotel-item" onClick={() => handleUnassign(hotel.hotel_id)}>
                                            {hotel.name} <span>&times;</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button type="button" className="submit-btn" onClick={handleSave} disabled={loading}>
                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default HotelAssignmentModal;